import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { useLang } from "../i18n";
import { MaskedLine, Reveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { SmartImage } from "../components/SmartImage";

const AP_KEYS = ["ap1", "ap2", "ap3", "ap4"];

export default function About() {
  const { t } = useLang();

  useEffect(() => {
    document.title = "About Us | Sana Steel Industries, Bengaluru";
  }, []);

  return (
    <main data-testid="about-page" className="pt-20">
      <section className="bg-navy py-24 md:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <MaskedLine delay={0.1}>
            <span className="section-eyebrow-dark inline-flex items-center gap-3">
              <span className="w-8 h-[2px] bg-silver inline-block" />
              {t("about.eyebrow")}
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-white leading-[0.95] text-5xl md:text-7xl">
            <MaskedLine delay={0.25}>{t("about.l1")}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-silver">{t("about.l2")}</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-7">
            <h2 className="font-heading text-3xl lg:text-4xl font-black uppercase tracking-tight text-navy">
              {t("about.story.h2")}
            </h2>
            <div className="mt-6 space-y-5 text-steel text-base leading-relaxed">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-gray-200 pt-8">
              {[
                ["09", t("stat.1")],
                ["100%", t("stat.2")],
                ["BLR", t("stat.3")],
              ].map(([v, l], i) => (
                <div key={l} data-testid={`about-stat-${i + 1}`}>
                  <p className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-navy">{v}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-steel">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-steel" />
              <SmartImage src={IMAGES.aboutWorkers} alt="Workers assembling Sana Steel scaffolding"
                          className="relative w-full h-[420px] object-cover" />
            </div>
            <div className="relative mt-10 hidden md:block">
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-navy" />
              <SmartImage src={IMAGES.aboutBlueprint} alt="Crew working on scaffolding at sunset"
                          className="relative w-full h-64 object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />

      <section className="py-24 md:py-32 bg-surface" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="mb-14">
            <p className="section-eyebrow">{t("about.pillars.eyebrow")}</p>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-black uppercase tracking-tight text-navy">
              {t("about.pillars.h2")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AP_KEYS.map((k, i) => (
              <Reveal key={k} delay={i * 0.07}>
                <div className="group bg-white border border-gray-200 p-8 md:p-10 transition-shadow duration-300 hover:shadow-[6px_6px_0_#0A1F44]"
                     data-testid={`about-pillar-0${i + 1}`}>
                  <span className="font-heading text-5xl font-black tracking-tighter text-gray-200 group-hover:text-steel transition-colors duration-300">
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 font-heading text-2xl font-black uppercase tracking-tight text-navy">{t(`${k}.t`)}</h3>
                  <p className="mt-3 text-steel text-sm leading-relaxed">{t(`${k}.d`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <Link to="/quote" data-testid="about-quote-button" className="btn-primary">
              {t("about.cta")} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
